import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core'
import {Observable, BehaviorSubject, of} from 'rxjs'
import {
  tap,
  map,
  switchMap,
  filter,
  pairwise,
  startWith,
  catchError,
} from 'rxjs/operators'
import {ContextService} from '@app/core/services/context.service'
import {DomainService} from '@app/core/services/rest-api'
import {AppAsideService} from '@app/features/page/app-aside/app-aside.service'

@Component({
  selector: 'domain-tree',
  templateUrl: './domain-tree.component.html',
  styleUrls: ['./domain-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DomainTreeComponent implements OnInit, OnDestroy {
  selectedDomain$: Observable<any>
  domains$: Observable<{
    domainTree: any[]
    searchMap: Object
    activeDomain: string
  }>
  filter$ = new BehaviorSubject('')
  highlight$ = new BehaviorSubject('')
  refreshTrigger$ = new BehaviorSubject(true)
  resetTrigger$ = new BehaviorSubject(true)
  initializing = true
  initTimestamp = Date.now()
  scrollIntoViewTimeout: number
  minFilterLength = 1
  isLoading$ = new BehaviorSubject(false)

  constructor(
    private context: ContextService,
    private domainService: DomainService,
    private appAsideService: AppAsideService,
  ) {}

  ngOnInit() {
    this.refreshTrigger$ = this.context.refreshTrigger
    this.isLoading$.next(true)

    const domains$ = this.domainService.getDomainsTree().pipe(
      map((domains) => {
        return domains.sort((a, b) => {
          return a.code.localeCompare(b.code)
        })
      }),
      tap(() => {
        this.isLoading$.next(false)
      }),
      switchMap((domains) => {
        return this.resetTrigger$.pipe(
          map(() => {
            const domainTree = []
            const searchMap = {}
            this.processDomains(domains, searchMap, domainTree)
            return {domainTree: domainTree, searchMap: searchMap}
          }),
          switchMap(({domainTree, searchMap}) => {
            return this.context.activeDomain$.pipe(
              map((activeDomain) => {
                this.resetToDefault(searchMap, activeDomain)
                return {
                  domainTree: domainTree,
                  searchMap: searchMap,
                  activeDomain: activeDomain,
                }
              }),
              switchMap(({domainTree, searchMap, activeDomain}) => {
                return this.filter$.pipe(
                  // no debounce when filter is empty (on load)
                  // this breaks on NG 17~ disbled so domains actually load again
                  // debounce(filter => filter ? timer(400) : EMPTY),
                  filter((filter) => {
                    // pass on zero length and equal or longer than minFilterLength
                    return (
                      filter.length === 0 ||
                      filter.length >= this.minFilterLength
                    )
                  }),
                  startWith(''),
                  pairwise(),
                  map(([prevFilter, curFilter]) => {
                    if (curFilter.length >= this.minFilterLength) {
                      searchMap[this.context.loginDomain].open = true
                      this.setFolded(searchMap)
                      this.filterItems(searchMap, curFilter)
                    } else if (prevFilter.length && curFilter.length === 0) {
                      this.resetToDefault(searchMap, activeDomain)
                      this.scrollIntoView(activeDomain)
                    }
                    this.highlight$.next(curFilter)
                    return {
                      domainTree: domainTree,
                      searchMap: searchMap,
                      activeDomain: activeDomain,
                    }
                  }),
                )
              }),
            )
          }),
        )
      }),
      tap((r) => {
        if (this.initializing) {
          this.scrollIntoView(r.activeDomain)
        }
        this.initializing = false
      }),
      catchError((err) => {
        console.error(err)
        this.isLoading$.next(false)
        return of(err)
      }),
    )

    this.domains$ = this.refreshTrigger$.pipe(
      switchMap(() => {
        return domains$
      }),
    )
  }

  ngOnDestroy() {
    if (this.scrollIntoViewTimeout) {
      clearTimeout(this.scrollIntoViewTimeout)
    }
  }

  resetToDefault(searchMap, activeDomain) {
    Object.keys(searchMap).forEach((code) => {
      searchMap[code].open = false
      searchMap[code].hidden = false
    })
    searchMap[this.context.loginDomain].open = true
    searchMap[this.context.loginDomain].hidden = false
    if (activeDomain === this.context.loginDomain) {
      // show children one level
      this.setChildrenVisible(searchMap, activeDomain)
      searchMap[activeDomain].open = true
    } else {
      // just open parents
      this.openParents(searchMap, activeDomain)
    }
  }

  setFolded(searchMap) {
    Object.keys(searchMap).forEach((code) => {
      searchMap[code].open = false
      searchMap[code].hidden = true
    })
    searchMap[this.context.loginDomain].open = false
    searchMap[this.context.loginDomain].hidden = true
  }

  filterItems(searchMap, filter) {
    Object.keys(searchMap).forEach((code) => {
      if (searchMap[code].code.toLowerCase().indexOf(filter) !== -1) {
        searchMap[code].open = false
        searchMap[code].hidden = false
        this.openParents(searchMap, code)
      }
    })
  }

  openParents(searchMap, selectedCode, openNode = false) {
    if (!selectedCode) {
      return false
    }
    searchMap[selectedCode].open = openNode
    searchMap[selectedCode].hidden = false
    if (
      searchMap[selectedCode].hasOwnProperty('parent') &&
      searchMap[selectedCode].parent
    ) {
      return this.openParents(
        searchMap,
        searchMap[selectedCode].parent.code,
        true,
      )
    }
    return false
  }

  setChildrenVisible(searchMap, parentCode) {
    searchMap[parentCode].children.forEach((child) => {
      child.open = false
      child.hidden = false
    })
  }

  scrollIntoView(selected) {
    this.scrollIntoViewTimeout = window.setTimeout(() => {
      document.getElementById(this.initTimestamp + selected).scrollIntoView({
        behavior: 'smooth',
      })
    }, 200)
  }

  onFilterChange(filter) {
    this.filter$.next(filter.toLowerCase())
  }

  refreshDomains() {
    this.initializing = true
    this.refreshTrigger$.next(true)
  }

  resetList() {
    this.resetTrigger$.next(true)
  }

  processDomains(domains, map, domainTree) {
    // pre-process all domains
    for (const domain of domains) {
      const code = domain.code
      map[code] = {
        code: domain.code,
        name: domain.name,
        domain: domain,
        open: false,
        hidden: false,
        children: [],
        labels: [],
      }
    }
    for (const domain of domains) {
      const code = domain.code
      if (domain.parentCode) {
        map[code].parent = map[domain.parentCode]
      }
      if (domain.parentCode && code !== this.context.loginDomain) {
        map[domain.parentCode].children.push(map[code])
      } else {
        domainTree.push(map[code])
      }
    }
  }

  pin(item) {
    this.context.changeActiveDomain(item.domain.code)
    this.appAsideService.toggleVisible()
  }
}
